import { useMemo, useState } from "react";
import { motion } from "motion/react";
import { ArrowRight, Loader2 } from "lucide-react";

import { type Message } from "shared/apiClient";
import { getText } from "shared/lib";
import { animationVariants } from "../lib";

import { createMessageEvent, useChannel } from "shared/channel";
import {
  CaptureEventType,
  useAccount,
  useCaptureEvent,
  useNoticeboardData,
} from "@/hooks";

import { Button } from "shared/components";
import { QuestionLoader } from "@/components/wizard/questionLoader";
import { YesNoQuestionList } from "@/components/yesNoQuestionList";

export type YesNoListQuestionData = {
  answer_prefix?: string | null;
  expert_to_render: {
    output_key?: string;
    expert_name: string;
  };
  next_button_label?: string | null;
  question_subtitle?: string | null;
  question_title?: string | null;
};

export interface YesNoListStepProps {
  disabled?: boolean;
  message: Message;
  showLoading?: boolean;
}

export function YesNoListStep({
  disabled,
  message,
  showLoading,
}: YesNoListStepProps) {
  const { captureEvent } = useCaptureEvent();

  const questionData = message.question_data as YesNoListQuestionData;

  const { channel } = useChannel();

  const { conversation } = useAccount();
  const { noticeboardData } = useNoticeboardData();

  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState<Array<boolean | null>>(() => []);

  const questions = (() => {
    const { expert_name = "", output_key = "" } =
      questionData.expert_to_render || {};

    if (noticeboardData?.[expert_name]?.parsed_output?.[output_key]) {
      const questions =
        noticeboardData[expert_name].parsed_output[output_key] || [];

      return Array.isArray(questions) ? questions : [];
    }

    return [];
  })();

  const allQuestionsAnswered = useMemo(() => {
    return questions.every((_, index) => answers[index] !== undefined);
  }, [answers, questions]);

  const handleClickNext = () => {
    if (channel && conversation?.id) {
      setSubmitting(true);

      const sentences: Array<string> = [];
      if (questionData.answer_prefix) {
        sentences.push(questionData.answer_prefix, "");
      }

      questions.forEach((question, index) => {
        sentences.push(`${index}. ${question}`, "");
        if (answers[index] === true) {
          sentences.push(`    ${getText("Yes")}`);
        } else if (answers[index] === false) {
          sentences.push(`    ${getText("No")}`);
        } else {
          sentences.push(`    ${getText("Unsure")}`);
        }
      });

      const messageDescriptor = createMessageEvent({
        conversation_id: conversation.id,
        text: sentences.join("\n"),
      });

      console.log(
        "<YesNoListStep> handleClickAcceptAndContinue()",
        messageDescriptor,
      );
      channel.sendMessage(messageDescriptor);

      captureEvent(CaptureEventType.USER_ANSWERED_QUESTION, {
        messageId: message.id,
      });
    }
  };

  const renderFooter = () => {
    return (
      <motion.div
        variants={animationVariants}
        animate={["buttons"]}
        className="mt-3 flex justify-end gap-2.5 md:mt-8"
      >
        <Button
          data-testid="next-button"
          disabled={submitting || disabled || !allQuestionsAnswered}
          onClick={handleClickNext}
          size="sm"
        >
          {submitting || showLoading ? (
            <>
              <Loader2 className="mr-3 animate-spin" data-testid="submitting" />
            </>
          ) : null}
          {getText(questionData.next_button_label || "Continue")}
          <ArrowRight size={16} />
        </Button>
      </motion.div>
    );
  };

  if (showLoading) {
    return (
      <div className="w-full md:w-[600px] md:max-w-[600px]">
        <QuestionLoader />
      </div>
    );
  }

  return (
    <div
      className="wizard-step-transition text-bodylg flex h-full w-full flex-col md:max-w-[600px]"
      data-testid="step-container-yes-no-list"
    >
      <YesNoQuestionList
        answers={answers}
        onChangeAnswers={setAnswers}
        questions={questions}
        subtitle={getText(questionData?.question_subtitle || "")}
        title={getText(questionData?.question_title || "")}
      />

      {renderFooter()}
    </div>
  );
}
